// Pickup availability
if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        this.errorHtml = this.querySelector('template').content.firstElementChild.cloneNode(true);
        this.info = this.querySelector('.js-pickup-availability-info');
        this.heading = this.querySelector('.js-pickup-availability-heading');
        this.storeLocator = document.querySelector('my-store-locator');
        this.storeLocatorDrawer = document.querySelector('my-store-locator-drawer');

        // Detectar si el componente está renderizado dentro del carrito
        this.isInCart = !!this.closest(
          'cart-items, cart-drawer, cart-drawer-items, #main-cart-items, #cart-drawer, .cart-items, .cart-drawer, [data-pickup-context="cart"]'
        );
      }

      getPickupResolution() {
        const key = this.dataset?.itemKey || '0';
        return this.storeLocator?.pickupResolutionByKey?.[String(key)] || null;
      }

      setHeading(text, { hidden = false } = {}) {
        if (!this.heading) return;
        const label = this.heading.querySelector('b') || this.heading;
        if (text) {
          label.textContent = text;
        }
        this.heading.hidden = hidden;
      }

      setConfirmedNoPickup(hidden) {
        this.classList.toggle('pickup-availability--no-pickup', hidden);
      }

      renderNotAvailable() {
        this.setConfirmedNoPickup(true);
        this.setHeading('', { hidden: true });
        this.info.innerHTML = '';
      }

      renderError() {
        this.setConfirmedNoPickup(false);
        this.setHeading('', { hidden: true });
        this.info.innerHTML = '';
        this.info.append(this.errorHtml.cloneNode(true));
      }

      renderIncomplete() {
        this.setConfirmedNoPickup(false);
        this.setHeading('', { hidden: true });
        this.info.innerHTML = '';
        const incompleteText = this.storeLocator?.dataset?.incompleteText || '';
        const refreshText = this.storeLocator?.dataset?.refreshText || '';
        const incompleteHtml = `
          <div>
            <p class="caption">${incompleteText}</p>
            <button type="button" class="js-pickup-availability-retry pickup-availability-button link link--text underlined-link">
              ${refreshText}
            </button>
          </div>
        `;
        const incompleteNode = new DOMParser().parseFromString(incompleteHtml, 'text/html').body.childNodes[0];
        this.info.append(incompleteNode);
        const retryButton = this.info.querySelector('.js-pickup-availability-retry');
        if (retryButton) {
          retryButton.addEventListener('click', (evt) => {
            evt.preventDefault();
            if (typeof this.storeLocator?.refresh === 'function') {
              this.storeLocator.refresh();
            }
          });
        }
      }

      renderLoading() {
        this.setConfirmedNoPickup(false);
        this.setHeading('', { hidden: true });
        this.info.innerHTML = '';
        const loadingText = this.storeLocator?.dataset?.loadingText || '';
        const loadingHtml = `
          <p class="caption">
            ${loadingText} <i class="fa fa-spinner fa-spin"></i>
          </p>
        `;
        const loadingNode = new DOMParser().parseFromString(loadingHtml, 'text/html').body.childNodes[0];
        this.info.append(loadingNode);
      }

      renderPreview(location) {
        const resolution = this.getPickupResolution();
        if (!resolution) {
          this.renderLoading();
          return;
        }
        if (resolution.status !== 'ready') {
          this.renderIncomplete();
          return;
        }
        if (resolution.hasPickup === false) {
          this.renderNotAvailable();
          return;
        }

        this.setConfirmedNoPickup(false);

        const pickupLabel =
          this.storeLocator?.dataset?.pickupLabel ||
          this.dataset?.pickupLabel ||
          '';
        this.setHeading(pickupLabel, { hidden: false });

        if (this.dataset?.itemKey && location) {
          const itemLocationsData = this.storeLocator.itemLocationsData[this.dataset.itemKey];
          if (itemLocationsData) {
            const itemLocation = itemLocationsData.find((itemLocation) => itemLocation.id === location.id);
            if (itemLocation) {
              location = itemLocation;
            }
          }
        }
        this.info.innerHTML = '';

        const buttonText = this.storeLocator.dataset.viewStoreAvailabilityText;
        let storeNameLineHtml = '';
        let statusHtml = '';
        let stockHtml = '';

        // En PDP el botón siempre se ve; en carrito solo se oculta cuando todo está OK.
        let showButton = true;

        if (location) {
          const itemsAdded = this.dataset?.itemQuantity ? parseInt(this.dataset.itemQuantity) : null;

          // El nombre de la tienda siempre va primero, como header
          storeNameLineHtml = `
            <p class="caption pickup-availability__line">
              <span class="pickup-availability__store-name">${location.name}</span>
            </p>
          `;

          let pickUpTimeTranslated = '';
          if (location.pickUpTime) {
            const key = location.pickUpTime;
            const languageKey = SDG.Data.language?.toLowerCase() || 'en';
            pickUpTimeTranslated = window.deliveryOptionsStrings?.[languageKey]?.[key] || location.pickUpTime;
          }

          if (location.available) {
            this.dataset.quantityAvailable = location.quantityAvailable;

            if (itemsAdded && itemsAdded > location.quantityAvailable) {
              // Cantidad solicitada excede stock: siempre mostrar botón (problema accionable)
              statusHtml = `
                <p class="caption pickup-availability__line">
                  <span class="pickup-availability__error">${this.dataset.error}</span>
                </p>
              `;
            } else {
              // Disponible y cantidad cabe: ocultar botón solo si estamos en carrito
              if (this.isInCart) {
                showButton = false;
              }
              statusHtml = `
                <p class="caption pickup-availability__line">
                  <span class="pickup-availability__time">${pickUpTimeTranslated}</span>
                </p>
              `;
              if (location.quantityAvailable < 4) {
                const stockText = this.storeLocator.dataset.lowStockHtml.replace('{{ quantity }}', location.quantityAvailable);
                stockHtml = `
                  <p class="caption pickup-availability__line">
                    <span class="pickup-availability__stock">${stockText}</span>
                  </p>
                `;
              }
            }
          } else if (location.available === false) {
            // Sin stock en la tienda: siempre mostrar botón
            statusHtml = `
              <p class="caption pickup-availability__line">
                <span class="pickup-availability__unavailable">${this.storeLocator.dataset.unavailableText}</span>
              </p>
            `;
          }
        }

        const buttonHtml = showButton ? `
          <button
            class="js-my-store-locator-drawer-btn pickup-availability-button link link--text underlined-link"
            aria-haspopup="dialog"
            ${this.dataset?.itemKey ? `data-item-key="${this.dataset.itemKey}"` : ''}
          >
            ${buttonText}
          </button>
        ` : '';

        const infoHtml = `
          <div>
            ${storeNameLineHtml}
            ${statusHtml}
            ${stockHtml}
            ${buttonHtml}
          </div>
        `;

        const infoNode = new DOMParser().parseFromString(infoHtml, 'text/html').body.childNodes[0];
        this.info.append(infoNode);

        const button = this.querySelector('button');
        if (button) {
          button.addEventListener('click', (evt) => {
            evt.preventDefault();
            // set the location data to the item specific location data if it exists
            this.storeLocatorDrawer.selectedItemLocationsKey = null;
            if (this.dataset?.itemKey) {
              const selectedItemLocationsData = this.storeLocator.itemLocationsData[this.dataset.itemKey];
              if (selectedItemLocationsData) {
                this.storeLocatorDrawer.selectedItemLocationsKey = this.dataset.itemKey;
                this.storeLocatorDrawer.updateStoreDetails(selectedItemLocationsData, '');
              }
            }
            this.storeLocatorDrawer.show(evt.currentTarget);
          });
        }
      }
    }
  );
}